import Link from 'next/link';
import React, { useEffect } from 'react';
import { memo } from 'react';
import {
  Handle,
  Node,
  Position,
  useUpdateNodeInternals,
  useViewport,
} from '@xyflow/react';
import { cn } from '@/lib/utils';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useParams } from 'next/navigation';
import { TooltipContent } from '../components/TooltipContent';
import { slugify } from '../../../lib/map/slugify';
import { EntitreeData, EntitreeNode } from '../../../typesV2';
import { useAtom, useAtomValue } from 'jotai';
import {
  isLoadingNavAtom,
  contentAtom,
  isStreamingMapAtom,
  mobileSidebar,
} from '../../../jotaiStore';
import {
  showNavigationWarningToast,
  dismissNavigationWarningToast,
} from '@/lib/toast';
import LoadingSpinnerSmall from '../../../features/global/loading/LoadingSpinnerSmall';
import getContentLink from '@/lib/map/getContentLink';
const { Top, Bottom, Left, Right } = Position;

interface SubtopicProps {
  topicNumber?: number;
  number: number;
  title: string;
  description: string;
  mapSlug: string;
}

const SubtopicLink = ({
  topicNumber,
  number,
  title,
  description,
  mapSlug,
}: SubtopicProps) => {
  const { x, y, zoom } = useViewport();
  const [, setIsLoadingNav] = useAtom(isLoadingNavAtom);
  const [, setContentData] = useAtom(contentAtom);
  const isStreamingMap = useAtomValue(isStreamingMapAtom);
  const [showMobileSidebar, setShowMobileSidebar] = useAtom(mobileSidebar);
  const { contentSlug } = useParams();
  const contentLink = getContentLink(
    x,
    y,
    zoom,
    contentSlug as string,
    mapSlug,
    title
  );

  return (
    <Tooltip.Root delayDuration={100}>
      <Link
        href={contentLink}
        className=""
        onClick={(e) => {
          if (isStreamingMap) {
            e.preventDefault();
            showNavigationWarningToast();
          } else {
            if (showMobileSidebar) setShowMobileSidebar(false);
            setIsLoadingNav(true);
            setContentData('');
          }
        }}
      >
        <div className="pointer-events-auto">
          <Tooltip.Trigger
            asChild
            id={`topic-${topicNumber}-subtopic-${number}-tooltip`}
            className="pointer-events-auto"
          >
            <div className="p-4 pt-5 transition-all rounded-md bg-shades-baseLighter hover:bg-shades-baseLightest">
              <p className="text-[20px] font-medium leading-4 tracking-wider text-colors-green">
                SECTION {number}
              </p>
              <h3 className="text-shades-textLight text-[26px] underline font-medium">
                {title}
              </h3>
            </div>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <TooltipContent
              title={title}
              description={description}
              number={number}
              mapSlug={mapSlug}
              labelText="SECTION"
              labelColor="text-colors-green"
              side="right"
            />
          </Tooltip.Portal>
        </div>
      </Link>
    </Tooltip.Root>
  );
};

// interface SectionNodeProps extends EntitreeNode {
//   sourcePosition?: Position;
//   data: {
//     coreTopicNumber: number;
//     topicTitle: string;
//     topicDescription: string;
//     sections?: Array<{
//       subTopicTitle: string;
//       subtopicDescription?: string;
//       sectionDescription?: string;
//     }>;
//   };
// }

const SectionNode = memo((props: EntitreeData) => {
  const { x, y, zoom } = useViewport();

  const { data } = props;
  const params = useParams();
  const mapSlug = Array.isArray(params.mapSlug)
    ? params.mapSlug[0]
    : params.mapSlug || '';

  const updateNodeInternals = useUpdateNodeInternals();

  // Needed to adjust position of the side quest source handle
  React.useEffect(() => {
    updateNodeInternals(props.id);
  }, []);

  return (
    <div className="max-w-md overflow-hidden border-[4px] rounded-lg bg-shades-base border-colors-green cursor-default">
      <Tooltip.Provider>
        {/* Source Handle */}
        <Handle type={'source'} position={Bottom} id={Bottom} />

        {/* Side Quest Source Handle */}
        <Handle
          // style={{ top: 400 }}
          type="source"
          position={props.sourcePosition || Right}
          id={props.sourcePosition || Right}
        />

        {/* Target Handle */}
        <Handle type={'target'} position={Top} id={Top} />

        <div className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center justify-center h-12 rounded-md min-w-12 bg-colors-green">
              <span className="text-3xl font-bold text-shades-baseDark mt-px">
                {data.coreTopicNumber}
              </span>
            </div>
            <h2 className="text-3xl font-bold text-shades-textLight">
              {data.topicTitle}
            </h2>
          </div>

          <p className="mb-6 text-3xl text-[28px] text-shades-text/90">
            {data.topicDescription}
          </p>

          <div className="">
            <div className="mb-3">
              <h3 className="text-[22px] font-semibold tracking-wider uppercase text-shades-text opacity-90">
                SECTIONS (
                <span className="text-[24px]">
                  {data.sections?.length || 0}
                </span>
                )
              </h3>
            </div>

            <div className="flex flex-col gap-2 space-y-4">
              {data.sections?.map((subtopic, index) => (
                <SubtopicLink
                  topicNumber={data.coreTopicNumber}
                  key={index}
                  number={index + 1}
                  title={subtopic.subTopicTitle}
                  description={subtopic.sectionDescription}
                  mapSlug={mapSlug}
                />
              ))}
            </div>
          </div>
        </div>
      </Tooltip.Provider>
    </div>
  );
});

SectionNode.displayName = 'SectionNode';

export default SectionNode;
